"use client";

// React & Nextjs imports
import React, { useState, useMemo, useCallback } from "react";

// Third party imports
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    TooltipItem,
    ChartData,
} from "chart.js";
import { format } from "date-fns";

// Models & Constants
import { FUNDAMENTAL_TABS, UsStock } from "@/app/interface/global";
import { CountryCurrency } from "@/app/interface/currency";

// Hooks & Utils & Contexts
import useMobile from "@/app/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useCulture } from "@/context/cultureProvider";
import {
    formatAmountToUS,
    formatAmountWithDecimal,
    formatCurrency,
    // getPureAmount,
    parseAmount,
} from "@/app/utils/currency-formatter";

// UI Components
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import BarChartAnnualQuarterlyMobile from "./bar-chart-annual-quarterly-mobile";
import ReturnsTable from "./returns-table";
import _ from "lodash";
import { useParams } from "next/navigation";
import { productRoute } from "@/types/product";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * Format date for chart labels
 */
export const formatChartDate = (date: Date): string => {
    return format(date, "MMM yyyy");
};

/**
 * Format price for display
 */
export const formatPrice = (price: number, cultureDetail: CountryCurrency): string => {
    return formatCurrency(price, cultureDetail.currencyCode, cultureDetail.locale);
};

const KeyFinancialTab = {
    RETURNS: "Returns",
    INCOME: "Income",
    BALANCE_SHEET: "Balance sheet",
} as const;

type KeyFinancialTabKey = keyof typeof KeyFinancialTab;
type KeyFinancialTabValue = (typeof KeyFinancialTab)[KeyFinancialTabKey];

const ReturnsTab = {
    RETURNS: "Returns",
} as const;

type ReturnsChartDataTable = {
    label: string;
    value: number;
};
type ReturnsChartDataChart = ChartData<"bar", (number | [number, number] | null)[], string>;

const returnsChartDataFormat = {
    TABLE: "TABLE",
    CHART: "CHART",
} as const;
type ReturnsChartDataFormat = keyof typeof returnsChartDataFormat;
/* 
const quarterlyIncomeChartData: {
    labels: ("Q1" | "Q2" | "Q3" | "Q4" | undefined)[];
    datasets: ({ */

const IncomeTab = {
    PROFIT: "Profit",
    REVENUE: "Revenue",
} as const;

const BalanceSheetTab = {
    ASSETS: "Assets",
    LIABILITIES: "Liabilities",
} as const;

const optionsTab = {
    ANNUAL: "Annual",
    QUARTERLY: "Quarterly",
} as const;

type optionsTabValues = (typeof optionsTab)[keyof typeof optionsTab];

const colorCode = {
    ORANGE: "#9195A9",
    GRAY: "#F15D2A",
} as const;

const MIN_BAR_HEIGHT = 15;

export interface Props {
    stockDetails: UsStock;
}

const KeyFinancials: React.FC<Props> = ({ stockDetails }) => {
    const financialData = stockDetails?.company_financials;
    const { cultureContext } = useCulture();
    const { countryProduct } = useParams();

    const { isMobile, hasLoaded } = useMobile();

    const [activeTab, setActiveTab] = useState<KeyFinancialTabValue>(KeyFinancialTab.INCOME);
    const [viewType, setViewType] = useState<optionsTabValues>(optionsTab.ANNUAL);

    const availableYears = useMemo(() => {
        if (activeTab === KeyFinancialTab.INCOME) {
            return [...new Set(financialData.income.quarters.map((item) => item.year.toString()))];
        } else if (activeTab === KeyFinancialTab.BALANCE_SHEET) {
            return [
                ...new Set(
                    financialData.balance_sheet.quarters.map((item) => item.year.toString()),
                ),
            ];
        }
        return [];
    }, [activeTab, financialData.income.quarters, financialData.balance_sheet.quarters]);

    const [selectedYear, setSelectedYear] = useState(
        availableYears[availableYears.length - 1 || 0],
    );

    const annualIncomeChartData = useMemo(() => {
        const lastThreeYearsIncome = [...financialData.income.annual]
            .sort((a, b) => Number(a.year) - Number(b.year))
            .slice(-3)
            .reverse();

        const years = lastThreeYearsIncome.map((item) => item.year.toString()).reverse();

        const revenueData = lastThreeYearsIncome.map((item) => parseAmount(item.revenue)).reverse();

        const profitData = lastThreeYearsIncome.map((item) => parseAmount(item.profit)).reverse();

        return {
            labels: years,
            datasets: [
                {
                    label: IncomeTab.PROFIT,
                    data: profitData,
                    backgroundColor: colorCode.GRAY,
                    borderRadius: { topLeft: 10, topRight: 10 },
                    barThickness: isMobile ? 15 : 30,
                    maxBarThickness: isMobile ? 10 : 10,
                    minBarLength: MIN_BAR_HEIGHT,
                },
                {
                    label: IncomeTab.REVENUE,
                    data: revenueData,
                    backgroundColor: colorCode.ORANGE,
                    borderRadius: { topLeft: 10, topRight: 10 },
                    barThickness: isMobile ? 15 : 30,
                    maxBarThickness: isMobile ? 10 : 10,
                    minBarLength: MIN_BAR_HEIGHT,
                },
            ],
        };
    }, [financialData.income, isMobile]);

    const quarterlyIncomeChartData = useMemo(() => {
        const quarters = financialData.income.quarters;
        const yearData = quarters.find((item) => item.year === Number(selectedYear));

        if (!yearData) return { labels: [], datasets: [] };

        const labels = quarters
            .map((item) => {
                if (item.year === Number(selectedYear)) {
                    return item.quarter;
                }
            })
            .filter((q) => q);

        const revenueData = quarters
            .map((item) => {
                if (item.year === Number(selectedYear)) {
                    return parseAmount(item.revenue) || 0.00001;
                }
            })
            .filter((q) => q);

        const profitData = quarters
            .map((item) => {
                if (item.year === Number(selectedYear)) {
                    return parseAmount(item.profit) || 0.00001;
                }
            })
            .filter((q) => q);

        return {
            labels,
            datasets: [
                {
                    label: IncomeTab.PROFIT,
                    data: profitData,
                    backgroundColor: colorCode.GRAY,
                    borderRadius: { topLeft: 10, topRight: 10 },
                    barThickness: isMobile ? 15 : 30,
                    maxBarThickness: isMobile ? 10 : 10,
                    minBarLength: MIN_BAR_HEIGHT,
                },
                {
                    label: IncomeTab.REVENUE,
                    data: revenueData,
                    backgroundColor: colorCode.ORANGE,
                    borderRadius: { topLeft: 10, topRight: 10 },
                    barThickness: isMobile ? 15 : 30,
                    maxBarThickness: isMobile ? 10 : 10,
                    minBarLength: MIN_BAR_HEIGHT,
                },
            ],
        };
    }, [financialData.income, selectedYear, isMobile]);

    const annualBalanceSheetChartData = useMemo(() => {
        const lastThreeYearsBalanceSheet = [...financialData.balance_sheet.annual]
            .sort((a, b) => Number(a.year) - Number(b.year))
            .slice(-3)
            .reverse();

        const years = lastThreeYearsBalanceSheet.map((item) => String(item.year)).reverse();

        const assetsData = lastThreeYearsBalanceSheet
            .map((item) => parseAmount(item.assets))
            .reverse();

        const liabilitiesData = lastThreeYearsBalanceSheet
            .map((item) => parseAmount(item.liabilities))
            .reverse();

        return {
            labels: years,
            datasets: [
                {
                    label: BalanceSheetTab.ASSETS,
                    data: assetsData,
                    backgroundColor: colorCode.GRAY,
                    borderRadius: { topLeft: 10, topRight: 10 },
                    barThickness: isMobile ? 15 : 30,
                    maxBarThickness: isMobile ? 10 : 10,
                    minBarLength: MIN_BAR_HEIGHT,
                },
                {
                    label: BalanceSheetTab.LIABILITIES,
                    data: liabilitiesData,
                    backgroundColor: colorCode.ORANGE,
                    borderRadius: { topLeft: 10, topRight: 10 },
                    barThickness: isMobile ? 15 : 30,
                    maxBarThickness: isMobile ? 10 : 10,
                    minBarLength: MIN_BAR_HEIGHT,
                },
            ],
        } as ChartData<"bar", (number | false)[], string>; // optional explicit cast
    }, [financialData.balance_sheet, isMobile]);

    const quarterlyBalanceSheetChartData = useMemo(() => {
        const quarters = financialData.balance_sheet.quarters;
        const yearData = quarters.find((item) => item.year === Number(selectedYear));

        if (!yearData) return { labels: [], datasets: [] };

        const labels = quarters
            .map((item) => item.year === Number(selectedYear) && item.quarter)
            .filter((q) => q);

        const assetsData = quarters
            .map((item) => item.year === Number(selectedYear) && parseAmount(item.assets))
            .filter((q) => q);

        const liabilitiesData = quarters
            .map((item) => item.year === Number(selectedYear) && parseAmount(item.liabilities))
            .filter((q) => q);

        return {
            labels,
            datasets: [
                {
                    label: BalanceSheetTab.ASSETS,
                    data: liabilitiesData,
                    backgroundColor: colorCode.GRAY,
                    borderRadius: { topLeft: 10, topRight: 10 },
                    barThickness: isMobile ? 15 : 30,
                    maxBarThickness: isMobile ? 10 : 10,
                    minBarLength: MIN_BAR_HEIGHT,
                },
                {
                    label: BalanceSheetTab.LIABILITIES,
                    data: assetsData,
                    backgroundColor: colorCode.ORANGE,
                    borderRadius: { topLeft: 10, topRight: 10 },
                    barThickness: isMobile ? 15 : 30,
                    maxBarThickness: isMobile ? 10 : 10,
                    minBarLength: MIN_BAR_HEIGHT,
                },
            ],
        } as ChartData<"bar", (number | false)[], string>; // optional explicit cast
    }, [financialData.balance_sheet, selectedYear, isMobile]);

    const returnsChartData = useCallback(
        (
            format: ReturnsChartDataFormat,
        ): ReturnsChartDataTable[] | ReturnsChartDataChart | undefined => {
            const labels = financialData.returns
                .map((item) => item.period)
                .map((item) => {
                    return item
                        .replace(/\b\s+(month|months)\b/i, "M")
                        .replace(/\b\s+(year|years)\b/i, "Y");
                });

            const data = financialData.returns.map((item) => item.percentage);

            if (!format || format === returnsChartDataFormat.TABLE) {
                const result = _.zipWith(labels, data, (label, value) => ({
                    label,
                    value,
                }));

                return result as ReturnsChartDataTable[];
            }

            return {
                labels,
                datasets: [
                    {
                        label: ReturnsTab.RETURNS,
                        data,
                        backgroundColor: financialData.returns.map((item) =>
                            item.is_positive ? colorCode.GRAY : colorCode.ORANGE,
                        ),
                        borderRadius: { topLeft: 10, topRight: 10 },
                        barThickness: isMobile ? 15 : 30,
                        maxBarThickness: isMobile ? 10 : 10,
                        minBarLength: MIN_BAR_HEIGHT,
                    },
                ],
            } as ReturnsChartDataChart;
        },
        [financialData.returns, isMobile],
    );

    const chartOptions: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                position: "bottom" as const,
                labels: {
                    boxWidth: 12,
                    padding: 20,
                    /* font: {
                        size: 12,
                        weight: "500" as const,
                    }, */
                    color: "#64748b",
                },
            },
            tooltip: {
                /* backgroundColor: "rgba(0, 0, 0, 0.8)",
                padding: 12,
                bodyFont: {
                    size: 12,
                }, */
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                padding: 12,
                titleFont: {
                    size: 12,
                    weight: 600 as unknown as "bold" | "normal" | "lighter" | "bolder",
                },
                bodyFont: {
                    size: 12,
                },
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    /* title(context) {
                        if (context[0]) {
                            const date = new Date(context[0].parsed.x as unknown as number);
                            return `${formatChartDate(date)} ${date && forecastDate && Number(date) === Number(forecastDate) ? "[Forecast]" : ""}`;
                        }
                        return "";
                    }, */
                    label(tooltipItem: TooltipItem<"bar">) {
                        if (activeTab === KeyFinancialTab.RETURNS) {
                            return ` ${tooltipItem.dataset.label ? tooltipItem.dataset.label + ":" : ""} ${tooltipItem.parsed.y}%`;
                        } else {
                            return ` ${tooltipItem.dataset.label ? tooltipItem.dataset.label + ":" : ""} ${formatAmountToUS(tooltipItem.parsed.y || 0, cultureContext.symbol)}`;
                        }
                    },
                },
            },
        },
        layout: {
            padding: {
                bottom: isMobile ? 0 : activeTab === KeyFinancialTab.RETURNS ? 0 : 45,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: "rgba(226, 232, 240, 0.5)",
                },
                ticks: {
                    color: "#64748b",
                    font: {
                        size: 11,
                    },
                    callback: function (value: unknown) {
                        return formatAmountWithDecimal(
                            formatAmountToUS(value as number, cultureContext.symbol),
                        );
                        /* return formatCurrency(
                            value as number,
                            cultureContext.currencyCode,
                            cultureContext.locale,
                        ).split(".")[0]; */
                    },
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: "#64748b",
                    font: {
                        size: 11,
                    },
                },
            },
        },
    };

    return (
        <>
            {isMobile && hasLoaded ? (
                <>
                    <ReturnsTable
                        returnsData={
                            returnsChartData(
                                returnsChartDataFormat.TABLE,
                            ) as ReturnsChartDataTable[]
                        }
                        activeTab={KeyFinancialTab.RETURNS}
                    />

                    {countryProduct === productRoute["us-stocks"] && (
                        <>
                            <BarChartAnnualQuarterlyMobile
                                stockDetails={stockDetails}
                                annualChartData={annualIncomeChartData}
                                quarterlyChartData={
                                    quarterlyIncomeChartData as ChartData<
                                        "bar",
                                        (number | false)[],
                                        string
                                    >
                                }
                                selectedYear={selectedYear}
                                setSelectedYear={setSelectedYear}
                                activeTab={KeyFinancialTab.INCOME}
                            />

                            <BarChartAnnualQuarterlyMobile
                                stockDetails={stockDetails}
                                annualChartData={annualBalanceSheetChartData}
                                quarterlyChartData={
                                    quarterlyBalanceSheetChartData as ChartData<
                                        "bar",
                                        (number | false)[],
                                        string
                                    >
                                }
                                selectedYear={selectedYear}
                                setSelectedYear={setSelectedYear}
                                activeTab={KeyFinancialTab.BALANCE_SHEET}
                            />
                        </>
                    )}
                </>
            ) : (
                <div className="mt-6 w-full max-w-full">
                    <h2 className="mb-6 text-3xl font-semibold xs:text-xl">{FUNDAMENTAL_TABS.KEY_FINANCIALS}</h2>

                    {(returnsChartData(
                        returnsChartDataFormat.TABLE,
                    ) as ReturnsChartDataTable[]) && (
                        <ReturnsTable
                            returnsData={
                                returnsChartData(
                                    returnsChartDataFormat.TABLE,
                                ) as ReturnsChartDataTable[]
                            }
                            activeTab={KeyFinancialTab.RETURNS}
                        />
                    )}

                    {countryProduct === productRoute["us-stocks"] && (
                        <Card className="min-h-[358px] rounded-xl py-4 xs:py-6">
                            {/* Tabs */}
                            {hasLoaded && (
                                <Tabs
                                    value={activeTab}
                                    onValueChange={(value) =>
                                        setActiveTab(value as KeyFinancialTabValue)
                                    }
                                    className="w-full"
                                >
                                    <div className="mb:px-6 relative mb-6 flex items-center justify-between border-b border-[#0E0F171A] xs:pt-0">
                                        <TabsList className="h-auto gap-6 border-b-0 bg-transparent p-0 xs:gap-8">
                                            {/* <TabsTrigger
                                                value={KeyFinancialTab.RETURNS}
                                                className="text-=[#9195A9] rounded-none border-b-2 border-transparent px-3 py-3 pl-4 text-sm font-medium data-[state=active]:border-[#CF5127] data-[state=active]:bg-transparent data-[state=active]:text-[#F15D2A] xs:px-5"
                                            >
                                                {KeyFinancialTab.RETURNS}
                                            </TabsTrigger> */}
                                            <TabsTrigger
                                                value={KeyFinancialTab.INCOME}
                                                className="rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-[#9195A9] data-[state=active]:border-[#CF5127] data-[state=active]:bg-transparent data-[state=active]:text-[#F15D2A] xs:px-5"
                                            >
                                                {KeyFinancialTab.INCOME}
                                            </TabsTrigger>
                                            <TabsTrigger
                                                value={KeyFinancialTab.BALANCE_SHEET}
                                                className="rounded-none border-b-2 border-transparent px-3 py-3 pr-4 text-sm font-medium text-[#9195A9] data-[state=active]:border-[#CF5127] data-[state=active]:bg-transparent data-[state=active]:text-[#F15D2A] xs:px-5"
                                            >
                                                {KeyFinancialTab.BALANCE_SHEET}
                                            </TabsTrigger>
                                        </TabsList>

                                        {/* Year Selector - only enabled for non-Returns & quarterly tabs */}
                                        {viewType === optionsTab.QUARTERLY && (
                                            <Select
                                                value={selectedYear}
                                                onValueChange={setSelectedYear}
                                            >
                                                <SelectTrigger
                                                    className="focus:outline-0! focus:ring-0! absolute -top-9 right-3 h-10 w-24 border-0 transition-all duration-200 xs:-top-2"
                                                    disabled={
                                                        activeTab === KeyFinancialTab.RETURNS ||
                                                        viewType !== optionsTab.QUARTERLY
                                                    }
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="">
                                                    {availableYears.map((year) => (
                                                        <SelectItem key={year} value={year}>
                                                            {year}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </div>

                                    {/* Annual/Quarterly Toggle - only for Income and Balance Sheet */}
                                    {activeTab !== KeyFinancialTab.RETURNS && (
                                        <div className="mx-4 mb-4 flex h-12 items-center justify-center rounded-full bg-[#F4F4F7] p-[calc((36px-28px)/2)] text-base font-medium xs:float-right xs:mx-6 xs:h-[36px] xs:max-w-[194px] xs:text-sm">
                                            <Button
                                                variant={
                                                    viewType === optionsTab.ANNUAL
                                                        ? "outline"
                                                        : null
                                                }
                                                size="sm"
                                                onClick={() => setViewType(optionsTab.ANNUAL)}
                                                className={cn(
                                                    "h-10 w-1/2 rounded-full border-[#0E0F171A] xs:h-7 xs:w-[93px]",
                                                    viewType === optionsTab.ANNUAL
                                                        ? "shadow-sm shadow-[#1018280D]"
                                                        : "text-[#9195A9]",
                                                )}
                                            >
                                                {optionsTab.ANNUAL}
                                            </Button>
                                            <Button
                                                variant={
                                                    viewType === optionsTab.QUARTERLY
                                                        ? "outline"
                                                        : null
                                                }
                                                size="sm"
                                                onClick={() => setViewType(optionsTab.QUARTERLY)}
                                                className={cn(
                                                    "h-10 w-1/2 rounded-full border-[#0E0F171A] xs:h-7 xs:w-[93px]",
                                                    viewType === optionsTab.QUARTERLY
                                                        ? "shadow-sm shadow-[#1018280D]"
                                                        : "text-[#9195A9]",
                                                )}
                                            >
                                                {optionsTab.QUARTERLY}
                                            </Button>
                                        </div>
                                    )}

                                    {/* Returns Tab */}
                                    {/* <TabsContent
                                        value={KeyFinancialTab.RETURNS}
                                        className="mt-0 xs:px-6"
                                    >
                                        <div className="mb-4 h-[36px] w-full">&nbsp;</div>
                                        <div className="min-h-[280px] overflow-hidden pl-2 transition-all duration-200">
                                            <Bar
                                                data={
                                                    returnsChartData(
                                                        returnsChartDataFormat.CHART,
                                                    ) as unknown as ReturnsChartDataChart
                                                }
                                                options={chartOptions}
                                                width={isMobile ? 40 : 58}
                                                height={isMobile ? 35 : 22}
                                            />
                                        </div>
                                    </TabsContent> */}

                                    {/* Income Tab */}
                                    <TabsContent
                                        value={KeyFinancialTab.INCOME}
                                        className="mt-0 xs:px-6"
                                    >
                                        <div className="min-h-[280px] pl-2 transition-all duration-200 xs:h-96">
                                            <Bar
                                                data={
                                                    viewType === optionsTab.ANNUAL
                                                        ? annualIncomeChartData
                                                        : quarterlyIncomeChartData
                                                }
                                                options={chartOptions}
                                                width={isMobile ? 40 : 58}
                                                height={isMobile ? 35 : 28}
                                            />
                                        </div>
                                    </TabsContent>

                                    {/* Balance Sheet Tab */}
                                    <TabsContent
                                        value={KeyFinancialTab.BALANCE_SHEET}
                                        className="mt-0 xs:px-6"
                                    >
                                        <div className="min-h-[280px] pl-2 transition-all duration-200 xs:h-96">
                                            <Bar
                                                data={
                                                    viewType === optionsTab.ANNUAL
                                                        ? annualBalanceSheetChartData
                                                        : quarterlyBalanceSheetChartData
                                                }
                                                options={chartOptions}
                                                width={isMobile ? 40 : 58}
                                                height={isMobile ? 35 : 28}
                                            />
                                        </div>
                                    </TabsContent>
                                </Tabs>
                            )}
                        </Card>
                    )}
                </div>
            )}
        </>
    );
};

export default KeyFinancials;
